import { Alert, Button, Popconfirm, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    Image as ZhImage
} from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { useUserStore } from "@/views/stores";
import type { SupplierRow } from "./types";

export interface SupplierFile {
    id?: number | string;
    path?: string;
}

export interface SupplierDetailPageProps {
    deleteSupplier?: (id: number | string) => Promise<void>;
    loadSupplier?: (id: number | string) => Promise<SupplierRow>;
    loadSupplierFiles?: (ids: number[]) => Promise<SupplierFile[]>;
    permissions?: string[];
}

/** 复用 Vue 供应商详情接口。 */
export async function loadSupplierDetail(id: number | string): Promise<SupplierRow> {
    const response = await request.service.get<SupplierRow>(`/admin/supplier-admin/find/${id}`);
    return response.data;
}

/** 复用 Vue 供应商删除接口，详情页只在具备处理权限时暴露入口。 */
export async function deleteSupplierDetail(id: number | string): Promise<void> {
    await request.service.delete(`/admin/supplier-admin/delete/${id}`);
}

/** 复用 Vue 文件批量接口，将文件 ID 转换为详情页可展示的媒体地址。 */
export async function loadSupplierFiles(ids: number[]): Promise<SupplierFile[]> {
    const response = await request.service.post<SupplierFile[]>("/dataService/admin/file/ids", ids);
    return response.data;
}

/** 将 Vue 供应商详情返回的类型和展示位置枚举统一为数字。 */
function normalizeSupplierDetail(result: SupplierRow): SupplierRow {
    return {
        ...result,
        supplier: result.supplier
            ? {
                  ...result.supplier,
                  type: result.supplier.type == null ? undefined : Number(result.supplier.type),
                  showLocation: result.supplier.showLocation == null ? undefined : Number(result.supplier.showLocation)
              }
            : undefined
    };
}

/** 供应商详情页，展示 Vue 详情页中的核心企业字段。 */
export function SupplierDetailPage({
    deleteSupplier: remove = deleteSupplierDetail,
    loadSupplier = loadSupplierDetail,
    loadSupplierFiles: loadFiles = loadSupplierFiles,
    permissions
}: SupplierDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_SUPPLIER_DEAL");
    const id = new URLSearchParams(location.search).get("id");
    const [supplier, setSupplier] = useState<SupplierRow["supplier"]>();
    const [hasConsignment, setHasConsignment] = useState(false);
    const [supplierFiles, setSupplierFiles] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        void loadSupplier(id)
            .then(result => {
                const normalizedResult = normalizeSupplierDetail(result);
                setSupplier(normalizedResult.supplier);
                setHasConsignment(Boolean(normalizedResult.productConsignment?.length));
                const fileIds = [
                    normalizedResult.supplier?.logoFileId,
                    normalizedResult.supplier?.businessFileId,
                    normalizedResult.supplier?.mobileSideFileId,
                    normalizedResult.supplier?.pcSideFileId
                ]
                    .filter(value => value != null)
                    .map(Number)
                    .filter(value => Number.isFinite(value));
                if (fileIds.length) {
                    void loadFiles(fileIds)
                        .then(files => {
                            setSupplierFiles(
                                files.reduce<Record<string, string>>((fileMap, file) => {
                                    if (file.id != null && file.path) fileMap[String(file.id)] = file.path;
                                    return fileMap;
                                }, {})
                            );
                        })
                        .catch(() => setSupplierFiles({}));
                }
            })
            .catch(() => setErrorMessage("供应商详情加载失败"));
    }, [id, loadFiles, loadSupplier]);

    const deleteCurrentSupplier = async () => {
        if (!id) return;
        try {
            // 删除失败时保留当前详情页，避免错误冒泡或误跳转到列表。
            await remove(id);
            void navigate("/supplierManagement/enterpriseManage");
        } catch {
            setErrorMessage("供应商删除失败");
        }
    };

    // 文件接口未返回地址时回退展示文件 ID，避免详情字段丢失。
    const renderSupplierFile = (id: number | string | undefined, alt: string) => {
        const path = id == null ? undefined : supplierFiles[String(id)];
        return path ? <ZhImage alt={alt} fit="contain" height="135px" src={path} width="240px" /> : id || "-";
    };

    return (
        <section aria-label="supplier-detail-page" className="flex min-h-full flex-col">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: supplier?.code || "-" }]}
                    rightInfos={[
                        { label: "创建时间", value: dateFormat(supplier?.createdTime, DATETIME_PICKER_FORMAT) }
                    ]}
                    title="供应商详情"
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {supplier ? (
                    <>
                        <div className="section-title mb-4 mt-6">基本信息</div>
                        <ZhGrid cols={4} gap={[50, 20]}>
                            <ZhBaseItem label="集团" value={supplier.groupCompanyName || "-"} />
                            <ZhBaseItem label="供应商名称" value={supplier.supplierName || "-"} />
                            <ZhBaseItem label="类型" value={supplier.type === 1 ? "企业" : "个人"} />
                            <ZhBaseItem
                                label="所在区域"
                                value={
                                    [
                                        [supplier.province, supplier.city, supplier.region].filter(Boolean).join(" "),
                                        [supplier.provinceCode, supplier.cityCode, supplier.regionCode]
                                            .filter(Boolean)
                                            .join(" / "),
                                        supplier.address
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || "-"
                                }
                            />
                            <ZhBaseItem label="展示位置" value={supplier.showLocation === 1 ? "首页" : "供应商列表"} />
                            <ZhBaseItem
                                label="企业标签"
                                value={supplier.labels?.length ? supplier.labels.join(";") : "-"}
                            />
                            <ZhBaseItem label="联系人" value={supplier.contactPerson || "-"} />
                            <ZhBaseItem label="联系电话" value={supplier.contactPhone || "-"} />
                        </ZhGrid>

                        <div className="section-title mb-4 mt-6">供应商信息</div>
                        <ZhGrid cols={4} gap={[50, 20]}>
                            <ZhBaseItem label="主营品类" span={4} value={supplier.mainCategories || "-"} />
                            <ZhBaseItem label="统一社会信用代码" value={supplier.socialCreditCode || "-"} />
                            <ZhBaseItem label="法人姓名" span={3} value={supplier.legalRepresentative || "-"} />
                            <ZhBaseItem label="企业 Logo" span={1}>
                                {renderSupplierFile(supplier.logoFileId, "企业 Logo")}
                            </ZhBaseItem>
                            <ZhBaseItem label="营业执照" span={1}>
                                {renderSupplierFile(supplier.businessFileId, "营业执照")}
                            </ZhBaseItem>
                            <ZhBaseItem label="供应商首页广告-移动端" span={1}>
                                {renderSupplierFile(supplier.mobileSideFileId, "移动端首页广告")}
                            </ZhBaseItem>
                            <ZhBaseItem label="供应商首页广告-PC端" span={1}>
                                {renderSupplierFile(supplier.pcSideFileId, "PC端首页广告")}
                            </ZhBaseItem>
                            <ZhBaseItem label="企业介绍" span={4} value={supplier.description || "-"} />
                        </ZhGrid>
                    </>
                ) : null}
                <Space className="mt-4">
                    {canWrite ? (
                        <Button
                            aria-label="编辑供应商"
                            onClick={() => void navigate(`/supplierManagement/enterpriseManage/edit?id=${id || ""}`)}
                        >
                            编辑
                        </Button>
                    ) : null}
                    {canWrite && !hasConsignment ? (
                        <Popconfirm
                            cancelText="取消"
                            okText="确定"
                            title="确认删除该供应商吗？"
                            onConfirm={() => void deleteCurrentSupplier()}
                        >
                            <Button aria-label="删除供应商" danger>
                                删除
                            </Button>
                        </Popconfirm>
                    ) : null}
                    <Button onClick={() => void navigate("/supplierManagement/enterpriseManage")}>返回</Button>
                </Space>
            </div>
        </section>
    );
}

export default SupplierDetailPage;
